import React, { useEffect, useMemo } from 'react';
import { NavigationContainer, getStateFromPath as getStateFromPathDefault, } from '@react-navigation/native';
import { View, Text, StyleSheet, StatusBar, Platform } from 'react-native';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import Config from 'react-native-config';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { store, persistor } from './src/redux/store';
import { environmentSelector, frontendBaseUrlSelector, userTokenSelector, } from './src/redux/appSlice';
import { DEFAULT_ENVIRONMENT, ENVIRONMENT_CONFIGS, } from './src/config/environment';
import { AppNav } from './src/navigation/AppNav';
import { GuestNav } from './src/navigation/GuestNav';
import { MovableFAB } from './src/components/MovableFAB';
import { PortionClarityToastHost } from './src/components/PortionClarityToastHost';
import { Button as AWButton } from './src/components/Button';
import { rootNavigationRef } from './src/navigation/rootNavigationRef';
import { SCREENSHOT_MODE } from './src/screenshotMode';
import { ScreenshotBootstrap } from './src/screenshotMode/ScreenshotBootstrap';
import { PaperProvider } from 'react-native-paper';
import * as Sentry from '@sentry/react-native';
import packageJson from './package.json';
import { navigationLightTheme, theme } from './src/theme';
import MaintenanceGate from './src/maintenance/MaintenanceGate';
import { HOME_TAB_PATHS } from './src/utils/homeTabs';
import { lockPortrait } from './src/utils/orientationLock';
const NavigationStack = () => {
    const userToken = useSelector(userTokenSelector);
    // #388: bug reporting is a tester tool; hide it on prod sessions.
    const isProd = useSelector(environmentSelector) === 'prod';
    return (<>
      {Platform.OS === 'web' ? (
        // Web: the maintenance banner sits in the layout and pushes the app down.
        <View style={styles.webColumn}>
          {!SCREENSHOT_MODE && <MaintenanceGate />}
          <View style={styles.webColumn}>
            {userToken ? <AppNav /> : <GuestNav />}
          </View>
        </View>) : (<>{userToken ? <AppNav /> : <GuestNav />}</>)}
      {/* Bug-report FAB and toasts are not App Store marketing material. */}
      {userToken && !SCREENSHOT_MODE && !isProd && <MovableFAB />}
      {userToken && !SCREENSHOT_MODE && <PortionClarityToastHost />}
      {SCREENSHOT_MODE && <ScreenshotBootstrap />}
      {Platform.OS !== 'web' && !SCREENSHOT_MODE && <MaintenanceGate />}
    </>);
};
const CrashFallback = (errorData) => {
    const error = errorData?.error;
    return (<View style={crashStyles.container}>
      <Text style={crashStyles.title}>Something went wrong</Text>
      <Text style={crashStyles.message}>
        The app hit an unexpected error. You can help by sending a crash report.
      </Text>
      <AWButton title="Send crash report" onPress={() => {
            // Use Sentry's feedback or fall back to a captured message
            const sentryAny = Sentry;
            if (typeof sentryAny.showFeedback === 'function') {
                sentryAny.showFeedback({
                    message: error?.message,
                });
            }
            else {
                Sentry.captureMessage('User sent crash feedback', {
                    level: 'error',
                    extra: {
                        message: error?.message,
                    },
                });
            }
        }}/>
    </View>);
};
Sentry.init({
    dsn: 'https://d3004ae77c2942b2e4d3ee30fe59598b@o4510275905191936.ingest.us.sentry.io/4510275907026944',
    // Release identification
    release: `${packageJson.name}@${packageJson.version}`,
    // Enable Native SDK (required for screenshots, view hierarchy)
    enableNative: true,
    // #382: no default PII (IP address, cookies, user), so crash reports aren't
    // linked to the user (matches App Privacy: Crash Data not linked).
    // See https://docs.sentry.io/platforms/react-native/data-management/data-collected/
    sendDefaultPii: false,
    // Enable Logs
    enableLogs: true,
    attachViewHierarchy: true,
    attachScreenshot: false,
    integrations: [
        Sentry.feedbackIntegration(),
        Sentry.reactNativeInfoIntegration(),
    ],
    // uncomment the line below to enable Spotlight (https://spotlightjs.com)
    // spotlight: __DEV__,
});
const App = () => {
    // Portrait everywhere; only the Meal Map screen unlocks rotation.
    useEffect(() => {
        lockPortrait();
    }, []);
    return (<SafeAreaProvider>
      <StatusBar barStyle="dark-content"/>
      <PaperProvider theme={theme}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <NavigationRoot />
          </PersistGate>
        </Provider>
      </PaperProvider>
    </SafeAreaProvider>);
};
const NavigationRoot = () => {
    const frontendBaseUrl = useSelector(frontendBaseUrlSelector) ||
        Config.FRONTEND_BASE_URL ||
        ENVIRONMENT_CONFIGS[DEFAULT_ENVIRONMENT].frontendBaseUrl;
    const userToken = useSelector(userTokenSelector);
    const linking = useMemo(() => ({
        prefixes: [
            frontendBaseUrl ||
                Config.FRONTEND_BASE_URL ||
                ENVIRONMENT_CONFIGS[DEFAULT_ENVIRONMENT].frontendBaseUrl,
        ],
        config: {
            screens: userToken
                ? {
                    Home: {
                        path: 'home',
                        screens: {
                            Profile: HOME_TAB_PATHS.Profile,
                            'Meal Map': HOME_TAB_PATHS['Meal Map'],
                            Summary: HOME_TAB_PATHS.Summary,
                            Trends: HOME_TAB_PATHS.Trends,
                            Photos: HOME_TAB_PATHS.Photos,
                            History: HOME_TAB_PATHS.History,
                        },
                    },
                    PasswordChange: {
                        path: 'password-change',
                        parse: {
                            hash: (hash) => decodeURIComponent(hash || ''),
                            email: (email) => decodeURIComponent(email || ''),
                        },
                    },
                    RequestAccountDeletion: 'request-deletion',
                }
                : {
                    Welcome: 'welcome',
                    Signin: 'signin',
                    Signup: 'signup',
                    ConfirmEmail: {
                        path: 'confirm-email',
                        parse: {
                            hash: (hash) => decodeURIComponent(hash || ''),
                        },
                    },
                    ForgotPassword: 'forgot-password',
                    RequestAccountDeletion: 'request-deletion',
                    PasswordChange: {
                        path: 'password-change',
                        parse: {
                            hash: (hash) => decodeURIComponent(hash || ''),
                            email: (email) => decodeURIComponent(email || ''),
                        },
                    },
                },
        },
        getStateFromPath(path, options) {
            const normalizedPath = path.length > 1
                ? path
                    .replace(/^\/([^/?#]+)/, (_m, segment) => {
                    return `/${segment.toLowerCase()}`;
                })
                    // /Home/Trends → /home/trends (tab segments are lowercase)
                    .replace(/^\/home\/([^/?#]+)/, (_m, segment) => {
                    return `/home/${segment.toLowerCase()}`;
                })
                : path;
            return getStateFromPathDefault(normalizedPath, options);
        },
    }), [frontendBaseUrl, userToken]);
    return (<Sentry.ErrorBoundary fallback={CrashFallback}>
      <NavigationContainer ref={rootNavigationRef} theme={navigationLightTheme} linking={linking} onStateChange={() => {
            // uncomment for stack debugging
            //console.log('NAV STATE:', rootNavigationRef.getRootState());
        }}>
        <NavigationStack />
      </NavigationContainer>
    </Sentry.ErrorBoundary>);
};
const crashStyles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    title: {
        fontSize: 20,
        fontWeight: '600',
        marginBottom: 8,
        textAlign: 'center',
    },
    message: {
        fontSize: 15,
        color: theme.colors.textCaption,
        marginBottom: 16,
        textAlign: 'center',
    },
});
export default Sentry.wrap(App);
const styles = StyleSheet.create({
    webColumn: { flex: 1, minHeight: 0 },
});
